// LogRocket network sanitizers (LP-2622): scrub PII, credentials and tokens from captured XHR/fetch traffic.
// Sanitizers only change LogRocket's captured copy; the application's real requests and responses are untouched.

import {SAFE_HEADERS} from './logrocket-pii-keys';
import {LogRocketRequest, LogRocketResponse} from './logrocket-privacy.types';
import {MAX_SANITIZED_BODY_LENGTH, REDACTED, redactJson} from './logrocket-redaction.util';
import {sanitizeLogRocketUrl} from './logrocket-url-sanitizer';

export function sanitizeHeaders(headers: {[key: string]: string | null | undefined} | null | undefined) {
    const result: {[key: string]: string | null | undefined} = {};
    for (const [name, value] of Object.entries(headers || {})) {
        result[name] = SAFE_HEADERS.has(name.toLowerCase()) ? value : REDACTED;
    }
    return result;
}

/**
 * JSON bodies keep their structure with every non-allowlisted value replaced. Anything else (form-encoded OAuth
 * credentials, multipart uploads, HTML, text) and oversized bodies are dropped; none of it is needed for replay.
 */
export function sanitizeBody(body: unknown, isResponse: boolean): string | undefined {
    if (body === null || body === undefined || body === '') {
        return undefined;
    }
    if (typeof body !== 'string' || body.length > MAX_SANITIZED_BODY_LENGTH) {
        return REDACTED;
    }
    try {
        return JSON.stringify(redactJson(JSON.parse(body), isResponse));
    } catch {
        return REDACTED;
    }
}

/** Returns a scrubbed copy, or null (LogRocket then drops the capture) if anything goes wrong. */
export function logRocketRequestSanitizer(request: LogRocketRequest): LogRocketRequest | null {
    try {
        return {
            ...request,
            url: sanitizeLogRocketUrl(request.url) ?? REDACTED,
            referrer: request.referrer === undefined ? undefined : sanitizeLogRocketUrl(request.referrer) ?? REDACTED,
            headers: sanitizeHeaders(request.headers),
            body: sanitizeBody(request.body, false),
        };
    } catch {
        return null;
    }
}

/** Returns a scrubbed copy, or null (LogRocket then drops the capture) if anything goes wrong. */
export function logRocketResponseSanitizer(response: LogRocketResponse): LogRocketResponse | null {
    try {
        return {
            ...response,
            url: response.url === undefined ? undefined : sanitizeLogRocketUrl(response.url) ?? REDACTED,
            headers: sanitizeHeaders(response.headers),
            body: sanitizeBody(response.body, true),
        };
    } catch {
        return null;
    }
}
